import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SparklesIcon, DocumentDuplicateIcon, CheckCircleIcon, ExclamationCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { parseCardInfo, formatCardNumber, validateCardNumber } from '../utils/generators';
import { useAppStore, getLastSavedText } from '../store/appStore';

interface CardDetails {
  format: string;
  cardNumber: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
}

const CardInformation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [appState, appStore] = useAppStore();
  
  // 局部状态（UI交互）
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [savedSuccessfully, setSavedSuccessfully] = useState(false);
  const [parseError, setParseError] = useState('');
  const [showDataSyncTip, setShowDataSyncTip] = useState(false);
  
  // 从全局store获取数据
  const { cardDetails } = appState.cardInformation;
  const [parsedExample] = useState('5598880380486821|06|2030|331');

  // 检查是否有其他页面的数据可以共享
  useEffect(() => {
    const { cardGeneration } = appState;
    if (cardGeneration.generatedCard && !cardDetails.cardNumber) {
      setShowDataSyncTip(true);
      setTimeout(() => setShowDataSyncTip(false), 8000);
    }
  }, [location, appState, cardDetails.cardNumber]);

  const handleParse = async () => {
    if (!cardDetails.format.trim()) {
      setParseError('请输入卡片信息');
      return;
    }

    setIsLoading(true);
    setParseError('');
    
    // 模拟解析延迟
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const parsed = parseCardInfo(cardDetails.format);
    
    if (parsed) {
      const newCardDetails = {
        ...cardDetails,
        cardNumber: parsed.cardNumber,
        expiryMonth: parsed.expiryMonth,
        expiryYear: parsed.expiryYear,
        cvv: parsed.cvv,
      };
      appStore.updateCardInformation({ cardDetails: newCardDetails });
      setErrors({});
      setParseError('');
    } else {
      setParseError('格式错误！请使用正确的格式：卡号|月份|年份|CVV');
    }
    
    setIsLoading(false);
  };

  const handleInputChange = (field: keyof CardDetails, value: string) => {
    const newCardDetails = { ...cardDetails, [field]: value };
    appStore.updateCardInformation({ cardDetails: newCardDetails });
    
    // 清除该字段的错误
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    
    // 清除解析错误
    if (field === 'format') {
      setParseError('');
    }
    
    setSavedSuccessfully(false);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!cardDetails.cardNumber.trim()) {
      newErrors.cardNumber = '卡号不能为空';
    } else if (!/^\d{13,19}$/.test(cardDetails.cardNumber)) {
      newErrors.cardNumber = '卡号必须是13-19位数字';
    } else if (!validateCardNumber(cardDetails.cardNumber)) {
      newErrors.cardNumber = '无效的卡号（Luhn算法验证失败）';
    }
    
    if (!cardDetails.expiryMonth.trim()) {
      newErrors.expiryMonth = '月份不能为空';
    } else if (!/^(0[1-9]|1[0-2])$/.test(cardDetails.expiryMonth)) {
      newErrors.expiryMonth = '月份必须是01-12';
    }
    
    if (!cardDetails.expiryYear.trim()) {
      newErrors.expiryYear = '年份不能为空';
    } else if (!/^\d{2}$/.test(cardDetails.expiryYear)) {
      newErrors.expiryYear = '年份必须是2位数字';
    }
    
    if (!cardDetails.cvv.trim()) {
      newErrors.cvv = 'CVV不能为空';
    } else if (!/^\d{3,4}$/.test(cardDetails.cvv)) {
      newErrors.cvv = 'CVV必须是3-4位数字';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setSavedSuccessfully(false);
    
    // 模拟保存延迟
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // 保存到全局store
    appStore.save();
    
    setIsLoading(false);
    setSavedSuccessfully(true);
    
    // 3秒后隐藏成功提示
    setTimeout(() => setSavedSuccessfully(false), 3000);
  };

  const handleClear = () => {
    appStore.clearModule('cardInformation');
    setErrors({});
    setParseError('');
    setSavedSuccessfully(false);
  };

  const handleModeChange = (mode: string) => {
    if (mode === 'generation') {
      navigate('/card-generation');
    }
  };

  const syncFromCardGeneration = () => {
    const { cardGeneration } = appState;
    if (cardGeneration.generatedCard) {
      const newCardDetails = {
        ...cardDetails,
        cardNumber: cardGeneration.generatedCard
      };
      appStore.updateCardInformation({ cardDetails: newCardDetails });
      setShowDataSyncTip(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* 数据同步提示 */}
      {showDataSyncTip && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <ClockIcon className="h-5 w-5 text-blue-600 mr-2" />
              <span className="text-blue-800 font-medium">发现卡号段生成页面的数据，是否同步到这里？</span>
            </div>
            <button 
              onClick={syncFromCardGeneration}
              className="text-sm bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 transition-colors"
            >
              同步数据
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <CreditCardIcon className="h-6 w-6 mr-2 text-primary-600" />
            虚拟卡配置
          </h2>
          <div className="flex items-center space-x-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="cardMode"
                value="generation"
                checked={false}
                onChange={() => handleModeChange('generation')}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-600">卡号段生成</span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="cardMode"
                value="complete"
                checked={true}
                onChange={() => handleModeChange('complete')}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">完整卡信息</span>
            </label>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-blue-900 mb-3 flex items-center">
              <DocumentDuplicateIcon className="h-5 w-5 mr-2" />
              粘贴卡片信息
            </h3>
            <div className="space-y-3">
              <div>
                <label className="label text-blue-900">格式：卡号|月份|年份|CVV</label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={cardDetails.format}
                    onChange={(e) => handleInputChange('format', e.target.value)}
                    className="input-field flex-1"
                    placeholder="输入完整卡片信息"
                  />
                  <button
                    onClick={handleParse}
                    disabled={isLoading}
                    className="btn-primary whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? '解析中...' : '解析'}
                  </button>
                </div>
                <p className="mt-2 text-xs text-blue-700">
                  示例: {parsedExample}
                </p>
                {parseError && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                    {parseError}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label">卡号</label>
              <input
                type="text"
                value={cardDetails.cardNumber}
                onChange={(e) => handleInputChange('cardNumber', e.target.value)}
                className={`input-field ${errors.cardNumber ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="13-19位卡号"
              />
              {cardDetails.cardNumber && (
                <p className="mt-1 text-xs text-gray-500">
                  格式化: {formatCardNumber(cardDetails.cardNumber)}
                </p>
              )}
              {errors.cardNumber && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.cardNumber}
                </p>
              )}
            </div>

            <div>
              <label className="label">月份</label>
              <input
                type="text"
                value={cardDetails.expiryMonth}
                onChange={(e) => handleInputChange('expiryMonth', e.target.value)}
                className={`input-field ${errors.expiryMonth ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="MM (01-12)"
                maxLength={2}
              />
              {errors.expiryMonth && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.expiryMonth}
                </p>
              )}
            </div>

            <div>
              <label className="label">年份</label>
              <input
                type="text"
                value={cardDetails.expiryYear}
                onChange={(e) => handleInputChange('expiryYear', e.target.value)}
                className={`input-field ${errors.expiryYear ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="YY (25-35)"
                maxLength={2}
              />
              {errors.expiryYear && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.expiryYear}
                </p>
              )}
            </div>

            <div>
              <label className="label">CVV</label>
              <input
                type="text"
                value={cardDetails.cvv}
                onChange={(e) => handleInputChange('cvv', e.target.value)}
                className={`input-field ${errors.cvv ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="CVV (3-4位)"
                maxLength={4}
              />
              {errors.cvv && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.cvv}
                </p>
              )}
            </div>
          </div>

          {/* 保存成功提示 */}
          {savedSuccessfully && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                  <span className="text-green-800 font-medium">卡片信息保存成功！</span>
                </div>
                <span className="text-sm text-green-600">
                  {getLastSavedText(appState.cardInformation.lastSaved)}
                </span>
              </div>
            </div>
          )}

          {/* 状态显示栏 */}
          <div className="mt-6 flex items-center justify-between text-sm text-gray-500 bg-gray-50 px-4 py-3 rounded-lg">
            <div className="flex items-center space-x-4">
              <span>数据状态: {appState.cardInformation.lastSaved ? '已保存' : '未保存'}</span>
              {appState.cardInformation.lastSaved && (
                <span className="flex items-center">
                  <ClockIcon className="h-4 w-4 mr-1" />
                  {getLastSavedText(appState.cardInformation.lastSaved)}
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <span>自动保存: {appState.settings.autoSave ? '开启' : '关闭'}</span>
              <div className={`w-2 h-2 rounded-full ${
                appState.settings.autoSave ? 'bg-green-500' : 'bg-gray-400'
              }`}></div>
            </div>
          </div>

          <div className="mt-8 flex justify-between items-center">
            <div className="flex space-x-2">
              <button 
                onClick={handleClear}
                className="btn-secondary"
                disabled={isLoading}
              >
                清空
              </button>
              <button 
                onClick={() => appStore.updateSettings({ autoSave: !appState.settings.autoSave })}
                className="btn-secondary text-sm"
              >
                {appState.settings.autoSave ? '关闭自动保存' : '开启自动保存'}
              </button>
            </div>
            <button 
              onClick={handleSave}
              disabled={isLoading}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  保存中...
                </>
              ) : (
                '保存配置'
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="card bg-gradient-to-br from-primary-50 to-blue-50 border-primary-200">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
              <SparklesIcon className="h-6 w-6 text-primary-600" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">使用提示</h3>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-start">
                <span className="text-primary-600 mr-2">•</span>
                <span>支持粘贴格式化的卡片信息，使用 | 分隔各字段</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-600 mr-2">•</span>
                <span>点击"解析"按钮自动填充各个字段</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-600 mr-2">•</span>
                <span>也可以手动输入每个字段的信息</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-600 mr-2">•</span>
                <span>所有信息将被安全加密存储</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

const CreditCardIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
  </svg>
);

export default CardInformation;
